import { useEffect, useState } from 'react'
import { Check, CircleX } from 'lucide-react'

// Minimal shared toast primitive (ENG-108). Module-level pub/sub so anything can
// `toast(...)`; mount one <Toaster/> in the app shell. Reusable later for
// save/copy feedback.
export type ToastTone = 'success' | 'error'
interface ToastItem { id: number; title: string; tone: ToastTone; onClick?: () => void }

let seq = 0
const listeners = new Set<(t: ToastItem) => void>()

export function toast(title: string, opts: { tone?: ToastTone; onClick?: () => void } = {}) {
  const item: ToastItem = { id: ++seq, title, tone: opts.tone ?? 'success', onClick: opts.onClick }
  listeners.forEach((l) => l(item))
}

export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([])

  useEffect(() => {
    const add = (t: ToastItem) => {
      setItems((prev) => [...prev, t])
      // Errors and toasts with an action stay until dismissed.
      if (t.tone === 'success' && !t.onClick) setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== t.id)), 5000)
    }
    listeners.add(add)
    return () => { listeners.delete(add) }
  }, [])

  const dismiss = (id: number) => setItems((prev) => prev.filter((x) => x.id !== id))

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 print:hidden">
      {items.map((t) => (
        <div
          key={t.id}
          className={cnTone(t.tone)}
        >
          {t.tone === 'error'
            ? <CircleX aria-hidden className="mt-0.5 size-4 shrink-0 text-danger" />
            : <Check aria-hidden className="mt-0.5 size-4 shrink-0 text-success" />}
          <button className="flex-1 text-left" onClick={() => { t.onClick?.(); dismiss(t.id) }}>
            {t.title}
          </button>
          <button className="text-muted-foreground hover:text-foreground" onClick={() => dismiss(t.id)} aria-label="Dismiss">×</button>
        </div>
      ))}
    </div>
  )
}

function cnTone(tone: ToastTone) {
  const base = 'flex min-w-[240px] max-w-sm items-start gap-2.5 rounded-lg border bg-card px-3.5 py-2.5 text-body shadow-[var(--shadow-card-hover)]'
  return tone === 'error' ? `${base} text-danger` : `${base} text-foreground`
}
